// Contextual hints — port of v1 js/hint-manager.js. Each hint retires once
// the move is performed, capped at maxShows; state lives in SaveV2
// (settings.hintsSeen, migrated from the v1 karateJump_hints key).

import type { EventBus } from '@/app/events';
import type { Lang } from '@/ui/i18n';
import type { SaveV2 } from '@/save/schema';

interface HintDef {
  maxShows: number;
  touch: { en: string; de: string };
  desktop?: { en: string; de: string };
}

const HINTS: Record<string, HintDef> = {
  controls: {
    maxShows: 2,
    touch: {
      en: 'Swipe ← → to switch lanes · Tap to jump',
      de: 'Wische ← → für Spurwechsel · Tippe zum Springen',
    },
    desktop: {
      en: '← → switch lanes · Space to jump',
      de: '← → Spurwechsel · Leertaste zum Springen',
    },
  },
  doubleJump: {
    maxShows: 5,
    touch: {
      en: 'Tap again in mid-air for a flip!',
      de: 'Tippe in der Luft erneut für einen Salto!',
    },
    desktop: {
      en: 'Jump again in mid-air for a flip!',
      de: 'Springe in der Luft erneut für einen Salto!',
    },
  },
  roofRide: {
    maxShows: 4,
    touch: { en: 'Land on car roofs for a bonus!', de: 'Lande auf Autodächern für einen Bonus!' },
  },
  construction: {
    maxShows: 3,
    touch: { en: 'Jump over the construction site!', de: 'Spring über die Baustelle!' },
  },
  combat: {
    maxShows: 2,
    touch: { en: 'Block the attack, then counter!', de: 'Blocke den Angriff, dann kontere!' },
  },
};

const HINT_DURATION = 3.2;
const HINT_COOLDOWN = 6.0;

export class HintSystem {
  private cooldown = 0;
  private visibleTimer = 0;
  private activeHintId: string | null = null;
  private readonly banner: HTMLElement;
  private readonly isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  constructor(
    root: HTMLElement,
    private readonly lang: Lang,
    private readonly save: SaveV2,
    private readonly persistSave: () => void,
    bus: EventBus,
  ) {
    this.banner = document.createElement('div');
    this.banner.id = 'hint-banner';
    root.appendChild(this.banner);

    // Learned-move retirements (v1 markDone call sites)
    bus.on('player:lane-changed', () => this.markDone('controls'));
    bus.on('player:jumped', ({ double }) => {
      if (double) this.markDone('doubleJump');
    });
    bus.on('player:roof-ride', () => this.markDone('roofRide'));
    bus.on('construction:cleared', () => this.markDone('construction'));
    bus.on('combat:started', () => {
      this.maybeShow('combat');
    });
    bus.on('combat:ended', ({ victory }) => {
      if (victory) this.markDone('combat');
    });
  }

  maybeShow(hintId: string): boolean {
    const hint = HINTS[hintId];
    if (!hint) return false;
    if (this.activeHintId || this.cooldown > 0) return false;

    const entry = this.save.settings.hintsSeen[hintId] ?? { shown: 0, done: false };
    if (entry.done || entry.shown >= hint.maxShows) return false;

    entry.shown++;
    this.save.settings.hintsSeen[hintId] = entry;
    this.persistSave();

    const variant = !this.isTouchDevice && hint.desktop ? hint.desktop : hint.touch;
    this.banner.textContent = this.lang === 'de' ? variant.de : variant.en;
    this.banner.classList.add('visible');
    this.activeHintId = hintId;
    this.visibleTimer = HINT_DURATION;
    return true;
  }

  markDone(hintId: string): void {
    const entry = this.save.settings.hintsSeen[hintId] ?? { shown: 0, done: false };
    if (entry.done) return;
    entry.done = true;
    this.save.settings.hintsSeen[hintId] = entry;
    this.persistSave();
    if (this.activeHintId === hintId) this.hide();
  }

  hide(): void {
    this.banner.classList.remove('visible');
    if (this.activeHintId) {
      this.activeHintId = null;
      this.cooldown = HINT_COOLDOWN;
    }
    this.visibleTimer = 0;
  }

  update(dt: number): void {
    if (this.activeHintId) {
      this.visibleTimer -= dt;
      if (this.visibleTimer <= 0) this.hide();
    } else if (this.cooldown > 0) {
      this.cooldown -= dt;
    }
  }
}
